import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { claimFeatureLesson, finishFeatureLesson, type FeatureLessonName, type FeatureLessonOutcome } from '../../services/featureLearningService';

/** Account receipts decide eligibility; the provider only coordinates the current UI. */
interface LearningContext {
  userId: string | null;
  claim: (id: string, lesson: FeatureLessonName) => Promise<boolean>;
  release: (id: string) => void;
  finish: (lesson: FeatureLessonName, outcome: FeatureLessonOutcome) => void;
}

const Context = createContext<LearningContext>({ userId: null, claim: async () => false, release: () => {}, finish: () => {} });

/** A new account receives a separate coordinator, including pending requests. */
interface LearningSession {
  active: string | null;
  claims: Map<FeatureLessonName, { owner: string; request: Promise<boolean> }>;
  outcomes: Set<string>;
}

/** Never queue a second card behind the current one or replay after a copy update. */
export function FeatureLearningProvider({ userId, children }: { userId: string | null; children: ReactNode }) {
  const session = useMemo<LearningSession>(() => ({ active: null, claims: new Map(), outcomes: new Set() }), [userId]);
  const release = useCallback((id: string) => {
    if (session.active === id) session.active = null;
  }, [session]);
  const claim = useCallback((id: string, lesson: FeatureLessonName): Promise<boolean> => {
    if (!userId || (session.active && session.active !== id)) return Promise.resolve(false);
    const previous = session.claims.get(lesson);
    if (previous && previous.owner !== id) return Promise.resolve(false);
    session.active = id;
    // Strict effects can subscribe twice to the same pending reservation.
    if (previous) return previous.request;
    const request = claimFeatureLesson(userId, lesson).catch((error: unknown) => {
      captureException(error instanceof Error ? error : String(error), 'FeatureLearning.claim', { userId, lesson });
      return false;
    }).then(allowed => {
      if (!allowed) release(id);
      return allowed;
    });
    session.claims.set(lesson, { owner: id, request });
    return request;
  }, [userId, session, release]);
  const finish = useCallback((lesson: FeatureLessonName, outcome: FeatureLessonOutcome) => {
    if (!userId) return;
    const key = `${lesson}:${outcome}`;
    if (session.outcomes.has(key)) return;
    session.outcomes.add(key);
    void finishFeatureLesson(userId, lesson, outcome).catch((error: unknown) => {
      session.outcomes.delete(key);
      captureException(error instanceof Error ? error : String(error), 'FeatureLearning.finish', { userId, lesson, outcome });
    });
  }, [userId, session]);
  const value = useMemo(() => ({ userId, claim, release, finish }), [userId, claim, release, finish]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/** Controls outside the signed-in learning boundary remain usable without an introduction. */
export const useFeatureLearning = () => useContext(Context);
